import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
    Truck, Package, CheckCircle2, Clock, XCircle, AlertTriangle,
    ChevronRight, X, Tag, MapPin, ArrowRight, FileText, Hash,
    Search, User, Phone, Coins, CalendarClock,
} from "lucide-react";

const STATUS_META = {
    awaiting_approval: { label: "Aguarda aprovação", color: "bg-gold-soft/20 text-gold-deep", icon: Clock },
    stock_confirmed: { label: "Stock confirmado", color: "bg-vineyard/15 text-vineyard", icon: CheckCircle2 },
    preparing: { label: "Em preparação", color: "bg-wine/10 text-wine", icon: Package },
    ready_to_ship: { label: "Pronto para envio", color: "bg-wine/15 text-wine", icon: Package },
    label_generated: { label: "Etiqueta gerada", color: "bg-gold-soft/30 text-gold-deep", icon: Tag },
    picked_up: { label: "Recolhido", color: "bg-vineyard/15 text-vineyard", icon: Truck },
    in_transit: { label: "Em trânsito", color: "bg-wine-dark/10 text-wine-dark", icon: Truck },
    out_for_delivery: { label: "Saiu para entrega", color: "bg-gold-soft/30 text-gold-deep", icon: Truck },
    delivered: { label: "Entregue", color: "bg-vineyard text-cream", icon: CheckCircle2 },
    returned: { label: "Devolvido", color: "bg-destructive/15 text-destructive", icon: XCircle },
    cancelled: { label: "Cancelado", color: "bg-wine-dark/10 text-wine-dark/60", icon: XCircle },
};

const NEXT_STATUS = {
    awaiting_approval: ["stock_confirmed", "cancelled"],
    stock_confirmed: ["preparing", "cancelled"],
    preparing: ["ready_to_ship", "cancelled"],
    ready_to_ship: ["label_generated", "picked_up", "cancelled"],
    label_generated: ["picked_up", "cancelled"],
    picked_up: ["in_transit"],
    in_transit: ["out_for_delivery", "returned"],
    out_for_delivery: ["delivered", "returned"],
    delivered: [],
    returned: [],
    cancelled: [],
};

const KANBAN_COLUMNS = [
    { id: "awaiting_approval", label: "Novos" },
    { id: "stock_confirmed", label: "A preparar" },
    { id: "preparing", label: "A preparar" },
    { id: "ready_to_ship", label: "Prontos" },
    { id: "label_generated", label: "Prontos" },
    { id: "in_transit", label: "Em trânsito" },
    { id: "out_for_delivery", label: "Em trânsito" },
    { id: "delivered", label: "Entregues" },
];

export default function VendorShipments() {
    const [shipments, setShipments] = useState([]);
    const [stats, setStats] = useState({});
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        const [s, st] = await Promise.all([
            api.get("/vendor/shipments"),
            api.get("/vendor/shipments/stats"),
        ]);
        setShipments(s.data);
        setStats(st.data);
    };
    useEffect(() => { load(); }, []);

    const byStatus = filter === "all" ? shipments : shipments.filter((s) => s.status === filter);
    const filtered = q
        ? byStatus.filter((s) => {
            const hay = `${s.id} ${s.tracking_code || ""} ${s.shipping_address?.full_name || ""} ${s.shipping_address?.city || ""}`.toLowerCase();
            return hay.includes(q.toLowerCase());
        })
        : byStatus;

    const newCount = stats.awaiting_approval || 0;
    const preparingCount = (stats.stock_confirmed || 0) + (stats.preparing || 0);
    const readyCount = (stats.ready_to_ship || 0) + (stats.label_generated || 0);
    const transitCount = (stats.picked_up || 0) + (stats.in_transit || 0) + (stats.out_for_delivery || 0);
    const lateCount = stats.late || 0;

    return (
        <section className="space-y-6" data-testid="vendor-shipments">
            <div>
                <span className="label-overline text-wine/60">Logística</span>
                <h1 className="font-heading text-3xl text-wine-dark">Envios</h1>
                <p className="text-sm text-wine-dark/60 mt-1">Gestão completa dos teus envios — aprova, prepara, adiciona tracking e acompanha até à entrega.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiBox label="Novos" value={newCount} accent />
                <KpiBox label="A preparar" value={preparingCount} />
                <KpiBox label="Prontos" value={readyCount} />
                <KpiBox label="Em trânsito" value={transitCount} />
                <KpiBox label="Atrasados" value={lateCount} warn />
            </div>

            {/* Search */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-3 flex items-center gap-3">
                <Search className="h-4 w-4 text-wine-dark/40 ml-1" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Procurar por #envio, cliente, cidade ou tracking…"
                    className="flex-1 bg-transparent text-sm focus:outline-none"
                    data-testid="shipments-search"
                />
                <span className="text-xs label-overline text-wine-dark/50">{filtered.length} resultados</span>
            </div>

            <div className="flex flex-wrap gap-1 bg-cream rounded-md p-1 w-fit">
                <FilterBtn current={filter} value="all" label={`Todos (${shipments.length})`} onClick={setFilter} />
                {Object.entries(STATUS_META).map(([k, m]) => (
                    (stats[k] || 0) > 0 && <FilterBtn key={k} current={filter} value={k} label={`${m.label} (${stats[k]})`} onClick={setFilter} />
                ))}
            </div>

            <div className="bg-white rounded-md border border-gold-soft/30 overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40">
                            <th className="py-3 px-4">Envio</th>
                            <th>Cliente</th>
                            <th>Itens</th>
                            <th className="text-right pr-2">Subtotal</th>
                            <th className="text-right px-2">Frete</th>
                            <th>Estado</th>
                            <th>Tracking</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((s) => {
                            const meta = STATUS_META[s.status] || STATUS_META.awaiting_approval;
                            const Icon = meta.icon;
                            return (
                                <tr key={s.id} onClick={() => setSelected(s)} className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer" data-testid={`shipment-row-${s.id}`}>
                                    <td className="py-3 px-4">
                                        <p className="font-mono text-xs text-wine-dark/80">#{s.id.slice(0, 8)}</p>
                                        <p className="text-[11px] text-wine-dark/50">{new Date(s.created_at).toLocaleDateString("pt-PT")}</p>
                                    </td>
                                    <td className="text-xs text-wine-dark/70">
                                        {(s.shipping_address?.full_name || "—")}<br />
                                        <span className="text-[10px] text-wine-dark/50">{s.shipping_address?.city}</span>
                                    </td>
                                    <td className="text-xs text-wine-dark/70">{s.items.length} item(ns)</td>
                                    <td className="text-right pr-2 text-wine-dark">€{s.subtotal.toFixed(2)}</td>
                                    <td className="text-right px-2 text-gold-deep">€{(s.shipping_cost || 0).toFixed(2)}</td>
                                    <td>
                                        <span className={`inline-flex items-center gap-1 label-overline px-2 py-1 rounded text-[10px] ${meta.color}`}>
                                            <Icon className="h-3 w-3" strokeWidth={1.5} /> {meta.label}
                                        </span>
                                    </td>
                                    <td className="text-xs text-wine-dark/70">
                                        {s.tracking_code ? (
                                            <span className="font-mono">{s.tracking_code}</span>
                                        ) : <span className="text-wine-dark/30">—</span>}
                                    </td>
                                    <td className="px-3 text-wine"><ChevronRight className="h-4 w-4" /></td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan="8" className="text-center py-10 text-wine-dark/60">Sem envios neste filtro.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selected && <ShipmentDrawer id={selected.id} onClose={() => setSelected(null)} onChange={() => { load(); }} />}
        </section>
    );
}

function FilterBtn({ current, value, label, onClick }) {
    return (
        <button onClick={() => onClick(value)} className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider whitespace-nowrap ${current === value ? "bg-wine text-cream" : "text-wine-dark/60 hover:text-wine"}`} data-testid={`filter-${value}`}>
            {label}
        </button>
    );
}

function KpiBox({ label, value, accent, warn }) {
    return (
        <div className={`p-4 rounded-md border ${accent ? "bg-wine-dark text-cream border-wine-dark" : warn && value > 0 ? "bg-destructive/5 border-destructive/30" : "bg-white border-gold-soft/30"}`}>
            <span className={`label-overline ${accent ? "text-gold-soft" : warn && value > 0 ? "text-destructive" : "text-wine/60"}`}>{label}</span>
            <p className={`font-heading text-3xl mt-1 ${warn && value > 0 ? "text-destructive" : ""}`}>{value}</p>
        </div>
    );
}

function ShipmentDrawer({ id, onClose, onChange }) {
    const [s, setS] = useState(null);
    const [carriers, setCarriers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [showTracking, setShowTracking] = useState(false);
    const [trackForm, setTrackForm] = useState({ carrier: "", tracking_code: "", estimated_delivery: "" });

    const load = async () => {
        const { data } = await api.get(`/vendor/shipments/${id}`);
        setS(data);
        if (!trackForm.carrier && data.carrier) {
            setTrackForm({ carrier: data.carrier, tracking_code: data.tracking_code || "", estimated_delivery: data.estimated_delivery || "" });
        }
    };
    useEffect(() => {
        load();
        api.get("/carriers").then((r) => setCarriers(r.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function transition(target) {
        if (target === "cancelled" && !window.confirm("Cancelar este envio?")) return;
        setBusy(true);
        try {
            await api.post(`/vendor/shipments/${id}/transition/${target}`, { note: "" });
            toast.success("Estado atualizado");
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    }

    async function saveTracking() {
        if (!trackForm.carrier || !trackForm.tracking_code) {
            toast.error("Transportadora e código obrigatórios");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/vendor/shipments/${id}/tracking`, trackForm);
            toast.success("Tracking adicionado");
            setShowTracking(false);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    }

    async function generateLabel() {
        setBusy(true);
        try {
            await api.post(`/vendor/shipments/${id}/label`);
            toast.success("Etiqueta gerada (mock — API real em breve)");
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    }

    if (!s) {
        return (
            <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-elegant/40" onClick={onClose}>
                <div className="bg-cream w-full max-w-xl h-full p-8" onClick={(e) => e.stopPropagation()}>A carregar…</div>
            </div>
        );
    }

    const meta = STATUS_META[s.status] || STATUS_META.awaiting_approval;
    const nextStatuses = NEXT_STATUS[s.status] || [];

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-elegant/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-cream w-full max-w-2xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="shipment-drawer">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <span className="label-overline text-wine">Envio</span>
                        <h2 className="font-heading text-3xl text-wine-dark mt-1">#{s.id.slice(0, 8)}</h2>
                        <span className={`inline-flex items-center gap-1.5 mt-2 label-overline px-2 py-1 rounded text-[10px] ${meta.color}`}>
                            {meta.label}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-wine-dark/60 hover:text-wine"><X className="h-5 w-5" /></button>
                </div>

                {/* Quick actions */}
                {nextStatuses.length > 0 && (
                    <div className="bg-white border border-gold-soft/40 rounded-md p-4 mb-5">
                        <span className="label-overline text-wine mb-3 block">Avançar para</span>
                        <div className="flex flex-wrap gap-2">
                            {nextStatuses.map((ns) => (
                                <button
                                    key={ns}
                                    onClick={() => transition(ns)}
                                    disabled={busy}
                                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs uppercase tracking-wider transition-colors disabled:opacity-50 ${ns === "cancelled" ? "bg-destructive/10 text-destructive hover:bg-destructive/20" : "bg-wine text-cream hover:bg-wine-dark"}`}
                                    data-testid={`transition-${ns}`}
                                >
                                    {STATUS_META[ns]?.label} <ArrowRight className="h-3 w-3" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tracking */}
                <div className="bg-white border border-gold-soft/40 rounded-md p-4 mb-5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="label-overline text-wine flex items-center gap-2"><Hash className="h-3 w-3" /> Tracking</span>
                        <button onClick={() => setShowTracking((v) => !v)} className="text-xs text-wine hover:text-gold-deep label-overline">
                            {s.tracking_code ? "Editar" : "Adicionar"}
                        </button>
                    </div>
                    {!showTracking ? (
                        s.tracking_code ? (
                            <div>
                                <p className="text-wine-dark font-mono">{s.tracking_code}</p>
                                <p className="text-xs text-wine-dark/60">{s.carrier_name || s.carrier}</p>
                                {s.estimated_delivery && (
                                    <p className="text-xs text-wine-dark/60 inline-flex items-center gap-1.5 mt-1">
                                        <CalendarClock className="h-3 w-3 text-wine/60" /> Entrega estimada: {new Date(s.estimated_delivery).toLocaleDateString("pt-PT")}
                                    </p>
                                )}
                                {s.tracking_url && (
                                    <a href={s.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-wine hover:text-gold-deep label-overline block mt-1">
                                        Abrir página da transportadora →
                                    </a>
                                )}
                            </div>
                        ) : (
                            <p className="text-xs text-wine-dark/55">Sem tracking adicionado.</p>
                        )
                    ) : (
                        <div className="space-y-3">
                            <label className="block">
                                <span className="label-overline text-wine-dark/60 text-[10px]">Transportadora</span>
                                <select value={trackForm.carrier} onChange={(e) => setTrackForm({ ...trackForm, carrier: e.target.value })} className="mt-1 w-full px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm" data-testid="track-carrier">
                                    <option value="">— Escolher —</option>
                                    {carriers.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="label-overline text-wine-dark/60 text-[10px]">Código de tracking</span>
                                <input value={trackForm.tracking_code} onChange={(e) => setTrackForm({ ...trackForm, tracking_code: e.target.value })} className="mt-1 w-full px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm font-mono" data-testid="track-code" placeholder="ex.: CTT123456789PT" />
                            </label>
                            <label className="block">
                                <span className="label-overline text-wine-dark/60 text-[10px]">Entrega estimada (opcional)</span>
                                <input type="date" value={trackForm.estimated_delivery || ""} onChange={(e) => setTrackForm({ ...trackForm, estimated_delivery: e.target.value })} className="mt-1 w-full px-3 py-2 bg-white border border-wine-dark/15 rounded-md text-sm" data-testid="track-eta" />
                            </label>
                            <div className="flex gap-2">
                                <button onClick={saveTracking} disabled={busy} className="btn-wine text-xs px-4 py-2 disabled:opacity-50" data-testid="save-tracking">Guardar</button>
                                <button onClick={() => setShowTracking(false)} className="text-xs text-wine-dark/60 px-4 py-2">Cancelar</button>
                            </div>
                        </div>
                    )}
                    {s.tracking_code && !showTracking && (
                        <button onClick={generateLabel} disabled={busy} className="mt-3 text-xs label-overline text-wine inline-flex items-center gap-1.5 hover:text-gold-deep" data-testid="generate-label">
                            <FileText className="h-3 w-3" /> Gerar etiqueta PDF (mock)
                        </button>
                    )}
                </div>

                {/* Items */}
                <Block title="Itens">
                    {s.items.map((it, i) => (
                        <div key={`${it.product_id || "item"}-${i}`} className="flex justify-between items-center text-sm border-b border-wine-dark/5 last:border-0 py-2">
                            <div>
                                <p className="text-wine-dark">{it.name}</p>
                                <p className="text-xs text-wine-dark/55">{it.quantity} × €{it.price.toFixed(2)}</p>
                            </div>
                            <span className="font-heading text-base text-wine-dark">€{it.subtotal.toFixed(2)}</span>
                        </div>
                    ))}
                </Block>

                {/* Financial summary */}
                <Block title="Resumo">
                    <SummaryRow label="Subtotal dos itens" value={`€${(s.subtotal || 0).toFixed(2)}`} />
                    <SummaryRow label="Frete cobrado ao cliente" value={`€${(s.shipping_cost || 0).toFixed(2)}`} />
                    <SummaryRow label="Comissão plataforma (15%)" value={`−€${((s.subtotal || 0) * 0.15).toFixed(2)}`} muted />
                    <div className="flex justify-between items-center pt-3 mt-2 border-t border-wine-dark/10">
                        <span className="label-overline text-vineyard inline-flex items-center gap-1.5"><Coins className="h-3.5 w-3.5" /> A receber (líquido)</span>
                        <span className="font-heading text-xl text-vineyard">€{((s.subtotal || 0) * 0.85).toFixed(2)}</span>
                    </div>
                </Block>

                {/* Shipping address */}
                {s.shipping_address && (
                    <Block title="Cliente & morada de entrega">
                        <p className="text-sm text-wine-dark flex items-center gap-2">
                            <User className="h-3.5 w-3.5 text-wine/60" /> {s.shipping_address.full_name}
                        </p>
                        {s.shipping_address.phone && (
                            <p className="text-xs text-wine-dark/65 flex items-center gap-2 mt-1">
                                <Phone className="h-3.5 w-3.5 text-wine/60" /> {s.shipping_address.phone}
                            </p>
                        )}
                        <div className="mt-2 pt-2 border-t border-wine-dark/5 flex items-start gap-2">
                            <MapPin className="h-3.5 w-3.5 text-wine/60 mt-0.5" />
                            <div>
                                <p className="text-xs text-wine-dark/65">{s.shipping_address.street}</p>
                                <p className="text-xs text-wine-dark/65">{s.shipping_address.postal_code} {s.shipping_address.city}{s.shipping_address.country ? ` · ${s.shipping_address.country}` : ""}</p>
                            </div>
                        </div>
                    </Block>
                )}

                {/* Timeline */}
                <Block title="Histórico">
                    <div className="space-y-3">
                        {[...(s.timeline || [])].reverse().map((ev, i) => {
                            const evMeta = STATUS_META[ev.status] || {};
                            const EvIcon = evMeta.icon || Clock;
                            return (
                                <div key={`${ev.at}-${ev.status}`} className="flex gap-3">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? "bg-wine text-cream" : "bg-cream text-wine-dark/50"}`}>
                                        <EvIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1 pb-3 border-b border-wine-dark/5 last:border-0">
                                        <p className="text-sm text-wine-dark font-semibold">{evMeta.label || ev.status}</p>
                                        <p className="text-[11px] text-wine-dark/50">{new Date(ev.at).toLocaleString("pt-PT")}</p>
                                        {ev.note && <p className="text-xs text-wine-dark/65 italic mt-0.5">"{ev.note}"</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Block>
            </div>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div className="mb-5 bg-white border border-gold-soft/30 rounded-md p-4">
            <h3 className="label-overline text-wine mb-3">{title}</h3>
            {children}
        </div>
    );
}

function SummaryRow({ label, value, muted }) {
    return (
        <div className="flex justify-between items-center py-1">
            <span className={`text-sm ${muted ? "text-wine-dark/55" : "text-wine-dark/75"}`}>{label}</span>
            <span className={`text-sm ${muted ? "text-gold-deep" : "text-wine-dark font-semibold"}`}>{value}</span>
        </div>
    );
}
